import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Copy, CopyPlus, Plus, Save, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import { payrollAPI } from '@/lib/api';

const MONTH_LABELS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const SiteChips = ({ sites, selected, onToggle, testPrefix }) => (
    <div className="flex flex-wrap gap-1">
        {sites.map(s => {
            const on = selected.includes(s.id);
            return (
                <button
                    key={s.id}
                    type="button"
                    onClick={() => onToggle(s.id)}
                    className={`px-2 h-6 rounded-full text-[11px] font-semibold border ${on ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200'}`}
                    data-testid={`${testPrefix}-${s.name.replace('FE ', '').toLowerCase()}`}
                >{s.name.replace('FE ', '')}</button>
            );
        })}
    </div>
);

export default function PayrollPrimes({ monthSel, sites, onChanged }) {
    const [employees, setEmployees] = useState([]);
    const [primes, setPrimes] = useState({});
    const [edits, setEdits] = useState({}); // {`${empId}|field`: raw}
    const [newEmp, setNewEmp] = useState({ name: '', site_ids: [] });
    const [nameEdits, setNameEdits] = useState({}); // {empId: raw}
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(() => localStorage.getItem('payroll_primes_open') === '1');

    const toggleOpen = () => {
        setOpen(o => {
            const next = !o;
            try { localStorage.setItem('payroll_primes_open', next ? '1' : '0'); } catch { /* ignore */ }
            return next;
        });
    };

    const load = useCallback(() => {
        payrollAPI.primes(monthSel.year, monthSel.month)
            .then(r => { setEmployees(r.data.employees); setPrimes(r.data.primes); setEdits({}); })
            .catch(() => toast.error('Erreur de chargement des primes'));
    }, [monthSel]);

    useEffect(() => { load(); }, [load]);

    const val = (empId, field) => {
        const k = `${empId}|${field}`;
        if (k in edits) return edits[k];
        const v = primes[empId]?.[field];
        return v ? String(v) : '';
    };
    const num = (empId, field) => parseFloat(String(val(empId, field)).replace(',', '.')) || 0;
    const dirty = Object.keys(edits).length > 0;

    const handleAddEmployee = async () => {
        if (!newEmp.name.trim()) { toast.error('Nom requis'); return; }
        if (newEmp.site_ids.length === 0) { toast.error('Sélectionnez au moins un site'); return; }
        try {
            await payrollAPI.createEmployee(newEmp);
            setNewEmp({ name: '', site_ids: [] });
            toast.success('Employé ajouté');
            load();
        } catch { toast.error("Erreur lors de l'ajout"); }
    };

    const handleToggleSite = async (emp, siteId) => {
        const next = emp.site_ids.includes(siteId)
            ? emp.site_ids.filter(x => x !== siteId)
            : [...emp.site_ids, siteId];
        if (next.length === 0) { toast.error('Au moins un site requis'); return; }
        try {
            await payrollAPI.updateEmployee(emp.id, { name: emp.name, site_ids: next });
            setEmployees(list => list.map(e => e.id === emp.id ? { ...e, site_ids: next } : e));
            onChanged?.();
        } catch { toast.error('Erreur de mise à jour'); }
    };

    const handleRenameBlur = async (emp) => {
        const raw = nameEdits[emp.id];
        if (raw == null) return;
        const name = raw.trim();
        setNameEdits(prev => { const n = { ...prev }; delete n[emp.id]; return n; });
        if (!name || name === emp.name) return;
        try {
            await payrollAPI.updateEmployee(emp.id, { name, site_ids: emp.site_ids });
            setEmployees(list => list.map(e => e.id === emp.id ? { ...e, name } : e));
            toast.success('Nom mis à jour');
        } catch { toast.error('Erreur lors du renommage'); }
    };

    const handleMove = async (index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= employees.length) return;
        const next = [...employees];
        [next[index], next[target]] = [next[target], next[index]];
        setEmployees(next);
        try {
            await payrollAPI.reorderEmployees(next.map(e => e.id));
        } catch {
            toast.error("Erreur lors du repositionnement");
            load();
        }
    };

    const handleDelete = async (emp) => {
        if (!window.confirm(`Supprimer ${emp.name} et toutes ses primes ?`)) return;
        try {
            await payrollAPI.deleteEmployee(emp.id);
            toast.success('Employé supprimé');
            load();
            onChanged?.();
        } catch { toast.error('Erreur de suppression'); }
    };

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const entries = employees.map(e => ({
                employee_id: e.id,
                prime_annuelle: num(e.id, 'prime_annuelle'),
                prime_anciennete: num(e.id, 'prime_anciennete'),
            }));
            await payrollAPI.savePrimes(monthSel.year, monthSel.month, entries);
            toast.success('Primes enregistrées');
            load();
            onChanged?.();
        } catch { toast.error("Erreur d'enregistrement"); }
        finally { setSaving(false); }
    };

    const handleCopy = async (mode) => {
        try {
            const r = await payrollAPI.copyPrimes(monthSel.year, monthSel.month, mode);
            toast.success(mode === 'from_prev'
                ? `Mois précédent recopié (${r.data.copied} lignes)`
                : mode === 'to_prev'
                    ? `Recopié sur le mois précédent (${r.data.copied} lignes)`
                    : `Recopié sur les mois suivants (${r.data.copied} lignes)`);
            load();
            onChanged?.();
        } catch { toast.error('Erreur lors de la copie'); }
    };

    const totalMonth = employees.reduce((s, e) => s + num(e.id, 'prime_annuelle') + num(e.id, 'prime_anciennete'), 0);

    return (
        <Card data-testid="payroll-primes-card">
            <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={toggleOpen}
                        className="flex items-center gap-2 text-left"
                        data-testid="primes-collapse-toggle"
                    >
                        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        <CardTitle className="text-base">Primes personnel — {MONTH_LABELS[monthSel.month - 1]} {monthSel.year}</CardTitle>
                        {!open && (
                            <span className="text-xs text-slate-400 font-normal">
                                {employees.length} employé{employees.length > 1 ? 's' : ''} · {formatCurrency(totalMonth)}
                            </span>
                        )}
                    </button>
                    <div className="flex-1" />
                    {open && (<>
                    <Button variant="outline" size="sm" className="h-7" onClick={() => handleCopy('from_prev')} data-testid="primes-copy-prev">
                        <Copy className="w-3.5 h-3.5 mr-1.5" />Dupliquer le mois précédent
                    </Button>
                    <Button variant="outline" size="sm" className="h-7" onClick={() => handleCopy('to_prev')} data-testid="primes-copy-to-prev">
                        <Copy className="w-3.5 h-3.5 mr-1.5 -scale-x-100" />Recopier sur le mois précédent
                    </Button>
                    <Button variant="outline" size="sm" className="h-7" onClick={() => handleCopy('to_rest_of_year')} data-testid="primes-copy-rest">
                        <CopyPlus className="w-3.5 h-3.5 mr-1.5" />Recopier sur les mois suivants
                    </Button>
                    <Button size="sm" className="h-7 bg-emerald-500 hover:bg-emerald-600 text-white" onClick={handleSave} disabled={!dirty || saving} data-testid="primes-save-btn">
                        <Save className="w-3.5 h-3.5 mr-1.5" />{saving ? 'Enregistrement…' : 'Enregistrer'}
                    </Button>
                    </>)}
                </div>
            </CardHeader>
            {open && (
            <CardContent className="overflow-x-auto space-y-3">
                <table className="w-full text-sm min-w-[720px]" data-testid="primes-table">
                    <thead>
                        <tr className="text-xs text-slate-500 border-b">
                            <th className="text-left py-2 pr-2">Employé</th>
                            <th className="text-left px-2 py-2">Sites (temps partagé = parts égales)</th>
                            <th className="text-right px-2 py-2">Prime annuelle (€/mois)</th>
                            <th className="text-right px-2 py-2">Prime ancienneté (€/mois)</th>
                            <th className="text-right px-2 py-2">Total</th>
                            <th className="px-1 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {employees.length === 0 && (
                            <tr><td colSpan={6} className="py-4 text-center text-slate-400 text-xs">Aucun employé — ajoutez le personnel ci-dessous.</td></tr>
                        )}
                        {employees.map((emp, index) => (
                            <tr key={emp.id} className="border-b last:border-0">
                                <td className="py-1.5 pr-1 whitespace-nowrap">
                                    <div className="flex items-center gap-1">
                                        <div className="flex flex-col">
                                            <button type="button" onClick={() => handleMove(index, -1)} disabled={index === 0}
                                                className="text-slate-300 hover:text-slate-600 disabled:opacity-30"
                                                data-testid={`prime-move-up-${emp.id}`}>
                                                <ChevronUp className="w-3.5 h-3.5" />
                                            </button>
                                            <button type="button" onClick={() => handleMove(index, 1)} disabled={index === employees.length - 1}
                                                className="text-slate-300 hover:text-slate-600 disabled:opacity-30"
                                                data-testid={`prime-move-down-${emp.id}`}>
                                                <ChevronDown className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                        <Input
                                            value={nameEdits[emp.id] ?? emp.name}
                                            onChange={e => setNameEdits(prev => ({ ...prev, [emp.id]: e.target.value }))}
                                            onBlur={() => handleRenameBlur(emp)}
                                            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                                            className="w-40 h-8 text-xs font-medium"
                                            data-testid={`prime-emp-${emp.id}`}
                                        />
                                    </div>
                                </td>
                                <td className="px-2 py-1.5">
                                    <SiteChips sites={sites || []} selected={emp.site_ids} onToggle={(sid) => handleToggleSite(emp, sid)} testPrefix={`prime-site-${emp.id}`} />
                                </td>
                                {['prime_annuelle', 'prime_anciennete'].map(field => (
                                    <td key={field} className="px-2 py-1.5 text-right">
                                        <Input
                                            type="text"
                                            inputMode="decimal"
                                            value={val(emp.id, field)}
                                            onFocus={e => e.target.select()}
                                            onChange={e => setEdits(prev => ({ ...prev, [`${emp.id}|${field}`]: e.target.value }))}
                                            className="w-24 h-8 text-right text-xs tabular-nums ml-auto"
                                            placeholder="0"
                                            data-testid={`prime-input-${field}-${emp.id}`}
                                        />
                                    </td>
                                ))}
                                <td className="px-2 py-1.5 text-right font-semibold tabular-nums">
                                    {(num(emp.id, 'prime_annuelle') + num(emp.id, 'prime_anciennete')) > 0
                                        ? formatCurrency(num(emp.id, 'prime_annuelle') + num(emp.id, 'prime_anciennete'))
                                        : <span className="text-slate-300">—</span>}
                                </td>
                                <td className="px-1 py-1.5">
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={() => handleDelete(emp)} data-testid={`prime-delete-${emp.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {employees.length > 0 && (
                            <tr className="bg-slate-50 font-semibold">
                                <td className="py-2 pr-2" colSpan={4}>Total primes du mois</td>
                                <td className="px-2 py-2 text-right tabular-nums" data-testid="primes-month-total">{formatCurrency(totalMonth)}</td>
                                <td />
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Add employee */}
                <div className="flex flex-wrap items-center gap-2 border-t pt-3" data-testid="primes-add-row">
                    <UserPlus className="w-4 h-4 text-slate-400" />
                    <Input
                        value={newEmp.name}
                        onChange={e => setNewEmp(n => ({ ...n, name: e.target.value }))}
                        placeholder="Nom de l'employé"
                        className="w-48 h-8 text-xs"
                        data-testid="primes-new-name"
                    />
                    <SiteChips
                        sites={sites || []}
                        selected={newEmp.site_ids}
                        onToggle={(sid) => setNewEmp(n => ({
                            ...n,
                            site_ids: n.site_ids.includes(sid) ? n.site_ids.filter(x => x !== sid) : [...n.site_ids, sid],
                        }))}
                        testPrefix="primes-new-site"
                    />
                    <Button size="sm" variant="outline" className="h-8" onClick={handleAddEmployee} data-testid="primes-add-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" />Ajouter
                    </Button>
                </div>
                <p className="text-xs text-slate-400">
                    Les primes (Combo ne les intègre pas) sont toujours incluses dans les ratios : au mois en totalité, aux semaines au prorata des jours.
                    Employés multi-sites : montant réparti à parts égales entre les sites cochés.
                </p>
            </CardContent>
            )}
        </Card>
    );
}
